<template>
  <nav
    :class="[
      'w-full z-50 transition-all duration-500 backdrop-blur-xl left-0 top-0',
      isHome
        ? 'fixed'
        : isDark
          ? 'relative bg-zinc-950 border-b border-zinc-900'
          : 'relative bg-[#FDFCF8] border-b border-[#E5E0D8]',
      isHome && isScrolled
        ? isDark
          ? 'bg-zinc-950/80 border-b border-zinc-900 shadow-2xl shadow-black/10'
          : 'bg-[#FDFCF8]/80 border-b border-[#E5E0D8] shadow-xl shadow-[#E5E0D8]/30'
        : '',
      isHome && !isScrolled
        ? 'bg-transparent border-b border-transparent'
        : ''
    ]"
  >
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex items-center justify-between h-20">

        <router-link to="/" class="relative z-50 flex items-center transition-transform duration-300 hover:scale-[1.01]">
          <img
            class="h-10 w-auto transition-all duration-500 ease-out"
            :class="{ 'brightness-0 contrast-200': !isDark }"
            src="./assets/Bookmysalon_White.svg" 
            alt="BookMySalon Logo"
          >
        </router-link>

        <div class="hidden md:flex items-center gap-8">
          <div class="flex items-center gap-6 pr-6 border-r transition-colors duration-200"
            :class="isDark ? 'border-zinc-800/60' : 'border-[#E5E0D8]'">
            <a
              v-for="link in links"
              :key="link.href"
              :href="isHome ? link.href : '/' + link.href"
              :class="[
                'text-[11px] uppercase tracking-[0.15em] font-semibold transition-all duration-300 relative py-2 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[1px] after:transition-all hover:after:w-full',
                isDark
                  ? 'after:bg-[#D5CCBC] text-zinc-400 hover:text-white'
                  : 'after:bg-[#A69B88] text-[#7A7367] hover:text-[#2D2924]',
                (isHome && !isScrolled)
                  ? 'text-zinc-200 hover:text-white after:bg-[#D5CCBC]'
                  : ''
              ]"
            >
              {{ link.label }}
            </a>
          </div>

          <div class="flex items-center gap-4">
            <ThemeToggle />

            <div v-if="isLogin" class="relative">
              <button
                @click="dropdownOpen = !dropdownOpen"
                class="px-5 py-2.5 rounded-xl text-[11px] uppercase tracking-wider font-bold transition-all duration-300 border flex items-center gap-2 focus:outline-none"
                :class="(isHome && !isScrolled)
                  ? 'bg-zinc-900/40 hover:bg-zinc-800 text-white border-zinc-800/80 backdrop-blur-md'
                  : isDark
                    ? 'bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-800'
                    : 'bg-[#FDFCF8] hover:bg-[#F7F5F0] text-[#2D2924] border-[#E5E0D8]'"
              >
                <span>{{ user.prenom }}</span>
                <svg class="w-3 h-3 transition-transform duration-300" :class="{ 'rotate-180': dropdownOpen }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>

              <div v-if="dropdownOpen" class="absolute right-0 mt-2 w-52 rounded-xl shadow-2xl border p-1.5 backdrop-blur-xl z-50 transition-all duration-200"
                :class="isDark ? 'bg-zinc-950/95 border-zinc-900 text-white' : 'bg-white/95 border-[#E5E0D8] text-[#2D2924]'">
                <router-link to="/userAccount" @click="dropdownOpen = false" class="flex items-center gap-2.5 px-4 py-3 text-[11px] uppercase tracking-wider font-medium rounded-lg hover:bg-zinc-900/40 transition-colors">
                  <svg class="w-4 h-4 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  Mon Compte
                </router-link>
                <hr :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'" class="my-1">
                <button @click="logout(true)" class="w-full flex items-center gap-2.5 px-4 py-3 text-[11px] uppercase tracking-wider font-bold text-rose-500 rounded-lg hover:bg-rose-500/10 transition-colors text-left">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                  Déconnexion
                </button>
              </div>
            </div>

            <router-link
              v-else
              to="/signup"
              class="px-5 py-2.5 rounded-xl text-[11px] uppercase tracking-wider font-bold transition-all duration-300 border text-center"
              :class="(isHome && !isScrolled)
                ? 'bg-zinc-900/40 hover:bg-zinc-800 text-white border-zinc-800/80 backdrop-blur-md'
                : isDark
                  ? 'bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-800'
                  : 'bg-[#FDFCF8] hover:bg-[#F7F5F0] text-[#2D2924] border-[#E5E0D8]'"
            >
              Se connecter
            </router-link>

            <router-link
              to="/espace-pro"
              class="px-5 py-2.5 rounded-xl text-[11px] uppercase tracking-wider font-bold border transition-all duration-300 shadow-sm text-center"
              :class="(isHome && !isScrolled)
                ? 'bg-[#D5CCBC] hover:bg-white text-zinc-950 border-transparent'
                : isDark
                  ? 'bg-white hover:bg-[#D5CCBC] text-zinc-950 border-transparent'
                  : 'bg-[#2D2924] hover:bg-[#A69B88] text-white border-transparent'"
            >
              Espace Pro
            </router-link>
          </div>
        </div>

        <div class="md:hidden relative z-50">
          <button
            @click="mobileMenu = !mobileMenu"
            class="p-2 focus:outline-none flex flex-col justify-center items-center gap-1.5 w-8 h-8 group"
            :class="(isHome && !isScrolled) ? 'text-white' : isDark ? 'text-white' : 'text-[#2D2924]'"
            aria-label="Menu"
          >
            <span :class="['w-6 h-[1px] transition-all duration-300 origin-center', isDark || (isHome && !isScrolled) ? 'bg-white' : 'bg-[#2D2924]', mobileMenu ? 'rotate-45 translate-y-[3.5px]' : '']"></span>
            <span :class="['w-6 h-[1px] transition-all duration-300', isDark || (isHome && !isScrolled) ? 'bg-white' : 'bg-[#2D2924]', mobileMenu ? 'opacity-0 scale-0' : '']"></span>
            <span :class="['w-6 h-[1px] transition-all duration-300 origin-center', isDark || (isHome && !isScrolled) ? 'bg-white' : 'bg-[#2D2924]', mobileMenu ? '-rotate-45 -translate-y-[3.5px]' : '']"></span>
          </button>
        </div>

      </div>
    </div>

    <transition
      enter-active-class="transition duration-400 ease-out"
      enter-from-class="opacity-0 translate-y-[-15px]"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-300 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-[-15px]"
    >
      <div v-if="mobileMenu" class="fixed inset-0 z-40 flex flex-col justify-between p-6 pt-28 pb-8 md:hidden  h-screen"
        :class="isDark ? 'bg-zinc-950' : 'bg-[#FDFCF8]'">
        
        <div class="flex flex-col gap-4 text-left">
          <a
            v-for="link in links"
            :key="link.href"
            :href="isHome ? link.href : '/' + link.href"
            @click="mobileMenu = false"
            class="text-xl font-medium tracking-wide py-2 transition-colors border-b"
            :class="isDark
              ? 'text-zinc-300 border-zinc-900/60 hover:text-[#D5CCBC]'
              : 'text-[#2D2924] border-[#E5E0D8]/60 hover:text-[#A69B88]'"
          >
            {{ link.label }}
          </a>
        </div>
        
        <div class="flex flex-col gap-3 pt-6 border-t transition-colors duration-200 mt-12"
          :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'">
          
          <template v-if="isLogin">
            <router-link to="/userAccount" @click="mobileMenu = false" class="w-full py-3.5 px-4 rounded-xl border font-bold text-[11px] uppercase tracking-wider transition-colors duration-200 flex items-center justify-center gap-2"
              :class="isDark ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-[#E5E0D8] text-[#2D2924]'">
              <svg class="w-4 h-4 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              <span>Mon Compte ({{ user.prenom }})</span>
            </router-link>
            
            <button @click="logout(true); mobileMenu = false" class="w-full py-3.5 rounded-xl font-bold text-[11px] uppercase tracking-wider transition-colors duration-200 bg-rose-500/10 text-rose-500 border border-transparent flex items-center justify-center gap-2">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              <span>Déconnexion</span>
            </button>
          </template>

          <template v-else>
            <router-link to="/signup" @click="mobileMenu = false" class="w-full py-3.5 rounded-xl border font-bold text-[11px] uppercase tracking-wider transition-colors duration-200 flex items-center justify-center gap-2"
              :class="isDark ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-[#E5E0D8] text-[#2D2924]'">
              <svg class="w-4 h-4 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              Se connecter
            </router-link>
          </template>

          <router-link to="/espace-pro" @click="mobileMenu = false" class="w-full py-3.5 rounded-xl font-bold text-[11px] uppercase tracking-wider transition-colors duration-200 flex items-center justify-center gap-2"
            :class="isDark ? 'bg-white text-black' : 'bg-[#2D2924] text-white'">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            Espace Professionnel
          </router-link>
        </div>
      </div>
    </transition>
  </nav>

  <main class="min-h-screen transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950' : 'bg-[#FDFCF8]'">
    <router-view/>
  </main>

  <footer class="border-t pt-20 pb-12 relative z-10 font-light text-left transition-colors duration-200"
    :class="isDark
      ? 'bg-zinc-950 border-zinc-900 text-zinc-400'
      : 'bg-[#FDFCF8] border-[#E5E0D8] text-[#7A7367]'">
    <div class="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-12 gap-y-16">

      <div class="space-y-6">
        <img src="./assets/Bookmysalon_White.svg" alt="BookMySalon Logo" class="h-9 w-auto" :class="{ 'brightness-0 contrast-200': !isDark }">
        <p class="text-[11px] leading-relaxed font-light max-w-xs m-0" :class="isDark ? 'text-zinc-500' : 'text-[#8C8579]'">
          Infrastructure d'intermédiation et de réservation instantanée pour les écosystèmes coiffure, barber, soins holistiques et instituts spécialisés.
        </p>
      </div>

      <div>
        <h4 class="text-[10px] font-bold uppercase tracking-widest mb-6 pb-2 border-b transition-colors duration-200"
          :class="isDark ? 'text-white border-zinc-900' : 'text-[#2D2924] border-[#E5E0D8]'">Pour les clients</h4>
        <ul class="space-y-3 text-xs p-0 m-0 list-none font-light">
          <li><a href="#categories" class="transition-colors" :class="isDark ? 'hover:text-[#D5CCBC]' : 'hover:text-[#A69B88]'">Catégories de soins</a></li>
          <li><a href="#top-salons" class="transition-colors" :class="isDark ? 'hover:text-[#D5CCBC]' : 'hover:text-[#A69B88]'">Établissements Élite</a></li>
          <li><a href="#app" class="transition-colors" :class="isDark ? 'hover:text-[#D5CCBC]' : 'hover:text-[#A69B88]'">Plateforme Mobile Native</a></li>
          <li><a href="#faq" class="transition-colors" :class="isDark ? 'hover:text-[#D5CCBC]' : 'hover:text-[#A69B88]'">Centre d'Assistance</a></li>
        </ul>
      </div>

      <div>
        <h4 class="text-[10px] font-bold uppercase tracking-widest mb-6 pb-2 border-b transition-colors duration-200"
          :class="isDark ? 'text-white border-zinc-900' : 'text-[#2D2924] border-[#E5E0D8]'">Pour les professionnels</h4>
        <ul class="space-y-3 text-xs p-0 m-0 list-none font-light">
          <li><a href="/pro/signup" class="font-medium transition-colors" :class="isDark ? 'text-white hover:text-[#D5CCBC]' : 'text-[#2D2924] hover:text-[#A69B88]'">Rejoindre le Réseau</a></li>
          <li><a href="/pro/dashboard" class="transition-colors" :class="isDark ? 'hover:text-[#D5CCBC]' : 'hover:text-[#A69B88]'">Console Management</a></li>
          <li><a href="/pro/pricing" class="transition-colors" :class="isDark ? 'hover:text-[#D5CCBC]' : 'hover:text-[#A69B88]'">Grille de Tarification SaaS</a></li>
        </ul>
      </div>

      <div class="space-y-6">
        <div>
          <h4 class="text-[10px] font-bold uppercase tracking-widest mb-3 transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-[#2D2924]'">Newsletter Intelligence</h4>
          <p class="text-[11px] leading-relaxed m-0 transition-colors duration-200"
            :class="isDark ? 'text-zinc-500' : 'text-[#8C8579]'">Analyses de tendances beauté et lancements d'adresses exclusives.</p>
        </div>

        <form class="flex flex-col gap-2" @submit.prevent>
          <input
            type="email"
            placeholder="votre_adresse@email.com"
            class="w-full px-4 py-3 text-xs rounded-xl border focus:outline-none transition-all duration-300 font-light"
            :class="isDark
              ? 'border-zinc-900 bg-zinc-900/50 text-white placeholder-zinc-600 focus:border-zinc-700 focus:bg-zinc-900'
              : 'border-[#E5E0D8] bg-[#F7F5F0] text-[#2D2924] placeholder-[#8C8579] focus:border-[#D6D0C5]'"
          />
          <button class="w-full py-3 rounded-xl text-[10px] uppercase tracking-wider font-bold border transition-all duration-300"
            :class="isDark
              ? 'bg-zinc-900 hover:bg-white text-zinc-400 hover:text-black border-zinc-800'
              : 'bg-[#F7F5F0] hover:bg-[#2D2924] text-[#7A7367] hover:text-white border-[#E5E0D8]'">
            S'abonner
          </button>
        </form>

        <div class="flex items-center gap-4 pt-1 transition-colors duration-200"
          :class="isDark ? 'text-zinc-600' : 'text-[#8C8579]'">
          <a href="#" class="transition-colors duration-300" :class="isDark ? 'hover:text-white' : 'hover:text-[#2D2924]'">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
          </a>
          <a href="#" class="transition-colors duration-300" :class="isDark ? 'hover:text-white' : 'hover:text-[#2D2924]'">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
          <a href="#" class="transition-colors duration-300" :class="isDark ? 'hover:text-white' : 'hover:text-[#2D2924]'">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"></path><circle cx="4" cy="4" r="2"></circle></svg>
          </a>
        </div>
      </div>

    </div>

    <div class="max-w-7xl mx-auto px-6 border-t mt-16 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-[11px] transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60 text-zinc-600' : 'border-[#E5E0D8] text-[#8C8579]'">
      <p class="m-0">© 2026 BookMySalon. Architecture Développée par DataFuse.</p>
      <div class="flex flex-wrap gap-x-6 gap-y-2 justify-center font-normal">
        <a href="/terms" class="transition-colors" :class="isDark ? 'hover:text-zinc-400' : 'hover:text-[#2D2924]'">Clauses Légales</a>
        <a href="/privacy" class="transition-colors" :class="isDark ? 'hover:text-zinc-400' : 'hover:text-[#2D2924]'">Confidentialité</a>
        <a href="/cookies" class="transition-colors" :class="isDark ? 'hover:text-zinc-400' : 'hover:text-[#2D2924]'">Traceurs</a>
      </div>
    </div>
  </footer>
</template>
<script>
import { useTheme } from '@/composables/useTheme'
import ThemeToggle from '@/components/ThemeToggle.vue'

export default {
  components: {
    ThemeToggle
  },
  setup() {
    const { isDark } = useTheme()
    return { isDark }
  },
  data() {
    return {
      isScrolled: false,
      mobileMenu: false,
      links: [
        { label: 'Catégories', href: '#categories' },
        { label: 'Top salons', href: '#top-salons' },
        { label: 'Avantages', href: '#benefits' },
        { label: 'Application', href: '#app' }
      ],
      email: '',
      isPro: true,
      isLogin: false,
      dropdownOpen: false,
      sidebarOpen: false,
      dropdownUserOpen: false,
      dropdownUserMobileOpen: false,
      user: { nom: '', prenom: '', id: '' },
    };
  },
  computed: {
    routePath() {
      return window.location.pathname;
    },
    isHome() {
      return this.$route && this.$route.path === '/';
    }
  },
  watch: {
    // Écoute les changements de route pour attacher/détacher dynamiquement l'écouteur du scroll
    isHome(newVal) {
      if (newVal) {
        window.addEventListener('scroll', this.handleScroll);
        this.handleScroll();
      } else {
        window.removeEventListener('scroll', this.handleScroll);
        this.isScrolled = false;
      }
    }
  },
  mounted() {
    if (this.isHome) {
      window.addEventListener('scroll', this.handleScroll);
    }

    this.isPro = this.routePath.includes('/espace-pro');
    const token = localStorage.getItem('clientToken');
    const expiration = parseInt(localStorage.getItem('clientToken_expire'), 10);
    const now = Date.now();

    if (token && (!expiration || now < expiration)) {
      const newExpireTime = now + 4 * 60 * 60 * 1000;
      localStorage.setItem('clientToken_expire', newExpireTime.toString());

      fetch('https://bookmysalon-967a856b16b6.herokuapp.com/api/clients/verify-token', {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.message === 'Token valide') {
            this.isLogin = true;
            this.user = {
              nom: data.user.nom,
              prenom: data.user.prenom,
              id: data.user.id,
            };
            localStorage.setItem('USERID', data.user.id);
          } else {
            this.logout();
          }
        })
        .catch((err) => {
          console.error('Erreur lors de la vérification du token :', err);
          this.logout();
        });
    } else {
      this.logout();
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    logout(redirect = false) {
  localStorage.removeItem('clientToken');
  localStorage.removeItem('clientToken_expire');
  localStorage.removeItem('USERID');
  this.isLogin = false;
  this.dropdownOpen = false;
  this.mobileMenu = false; // Ferme aussi le menu mobile au cas où
  this.user = { nom: '', prenom: '', id: '' };

  // Redirige uniquement si demandé explicitement (déconnexion manuelle)
  if (redirect) {
    this.$router.push('/');
  }
},
    handleScroll() {
      this.isScrolled = window.scrollY > 40;
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    closeSidebar() {
      this.sidebarOpen = false;
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap');

/* Variables CSS pour les thèmes */
:root.dark {
  --bg-primary: #09090b;
  --bg-secondary: #18181b;
  --text-primary: #fafafa;
  --text-secondary: #a1a1aa;
  --accent: #D5CCBC;
}

:root.light {
  --bg-primary: #f9fafb;
  --bg-secondary: #ffffff;
  --text-primary: #111827;
  --text-secondary: #4b5563;
  --accent: #8B7355;
}

body {
  background-color: var(--bg-primary);
  color: var(--text-secondary);
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  margin: 0;
  padding: 0;
  transition: background-color 0.2s, color 0.2s;
}

#app {
  text-align: center;
}

h1 {
  font-size: 58px ;
  margin-bottom: 1rem !important;
  line-height: 115% !important;
}

h3 {
  font-size: 20px !important;
  margin-bottom: 1rem;
  line-height: 115% !important;
}

p {
  font-size: 16px !important;
  line-height: 155% !important;
}

button {
  font-family: 'Manrope', sans-serif;
}
</style>